// @flow

import { useEffect, useCallback, useRef } from 'react';
import * as WidgetPropsModel from '../../../models/widget-props';
import WidgetUI from '../../widgets-ui/row-heatmap';
import { buildWidgetDateString } from '../common/stringHelpers';
import { useHasWidgetChanged, useSafeState, useSafeVisible } from '../common/hooks';
import { formatNumbersWithUnits } from '../../../utils/formatNumbers';
import { processDateHeatmapWidget } from '@auravisionlabs/aura-pkg-report-widgets/dist/core/widgets/date-heatmap';
import { browserQueryAPI } from '../../../services/query-api-browser';
import { useWidgetRenderState } from '../../../hooks/use-widget-render-state';

const DateHeatmapWidget = ({
  widget,
  availableMetrics,
  uiOptions = {},
  reportWidgetId,
}: WidgetPropsModel.dateHeatmapT) => {
  const [loading, setLoading] = useSafeState(false);
  const [progress, setProgress] = useSafeState(0);
  const [error, setError] = useSafeState(false);
  const [autoTitle, setAutoTitle] = useSafeState('');
  const [tableRows, setTableRows] = useSafeState([]);
  const [tableColumns, setTableColumns] = useSafeState([]);
  const hasWidgetChanged = useHasWidgetChanged(widget);
  const [currentElement, isVisible] = useSafeVisible(
    (vi) => uiOptions.disableViewportLoading || vi > 0
  );
  const { reportLoading, reportComplete, reportError } = useWidgetRenderState(reportWidgetId);

  const title = widget.customTitle || autoTitle;

  const usingComps =
    !!widget.comparisonPeriod &&
    widget.comparisonPeriod.active &&
    widget.showChangeValue;

  const loadWidgetData = async () => {
    setError(false);
    setLoading(true);
    setProgress(0);
    reportLoading();

    try {
      // Build config for the package
      const config = {
        metrics: widget.metrics,
        locations: widget.locations,
        period: widget.period,
        comparisonPeriod: widget.comparisonPeriod,
        demographicFilter: widget.demographicFilter,
        aggregation: widget.aggregation,
        showChangeValue: widget.showChangeValue,
        ...(widget.customTitle && { customTitle: widget.customTitle }),
      };

      // Build dependencies
      const deps = {
        queryAPI: browserQueryAPI,
        availableMetrics,
        startOfWeek: uiOptions.startOfWeek || 'monday',
        onProgress: (completed, total) => setProgress(completed / total),
      };

      // Call the package processor
      const widgetData = await processDateHeatmapWidget(config, deps);

      // Set the title from package
      setAutoTitle(widgetData.title);

      // Extract columns (skip first "Metric" column)
      const columns = widgetData.columns.slice(1).map(col => ({
        label: col.header,
      }));
      setTableColumns(columns);

      // Extract rows and map to UI format
      const rows = widgetData.rows.map((row, rowIndex) => {
        const metricLabel = row[0].value;
        const cols = row.slice(1).map(cell => ({
          value: cell.value,
          changeValue: widget.showChangeValue ? cell.value : null,
          unitType: cell.unit,
        }));

        // Build description for the metric using the corresponding metric spec
        const metricSpec = widget.metrics[rowIndex];
        const demos = metricSpec?.demographicFilter
          ? Object.values(metricSpec.demographicFilter).filter(Boolean).join(" ")
          : '';

        // Description will be empty if disableDescription is set
        const description = uiOptions.disableDescription ? '' : demos;

        return {
          label: metricLabel,
          description,
          cols,
        };
      });

      setTableRows(rows);

      // Report completion to context - use package output directly
      reportComplete(widgetData);
    } catch (e) {
      console.error('Error loading date heatmap widget:', e);
      setError(true);
      reportError(e.message || 'Failed to load Date Heatmap widget');
    }

    setLoading(false);
  };

  const [fetchRequired, setFetchRequired] = useSafeState(true);
  const hasInitialFetchedRef = useRef(false);

  useEffect(() => {
    if (hasWidgetChanged && hasInitialFetchedRef.current) setFetchRequired(true);
  }, [hasWidgetChanged]);

  useEffect(() => {
    if (fetchRequired && isVisible) {
      setFetchRequired(false);
      hasInitialFetchedRef.current = true;
      loadWidgetData();
    }
  }, [widget, fetchRequired, isVisible]); // eslint-disable-line react-hooks/exhaustive-deps

  const subtitle = buildWidgetDateString(widget, true);

  return (
    <div ref={currentElement}>
      <WidgetUI
        errors={error}
        loading={loading}
        progress={progress}
        title={title}
        subtitle={subtitle}
        tableColumns={tableColumns}
        tableRows={tableRows}
        dataKey={widget.showChangeValue ? 'changeValue' : 'value'}
      />
    </div>
  );
};

export default DateHeatmapWidget;
